import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Badge } from '../components/ui/badge';
import { ArrowLeft, Plus, Calendar, Users } from 'lucide-react';
import { toast } from 'sonner';

const GestionGrupos = () => {
  const navigate = useNavigate();
  
  const [periodos, setPeriodos] = useState([]);
  const [grupos, setGrupos] = useState([]);
  const [niveles, setNiveles] = useState([]);
  const [especialidades, setEspecialidades] = useState([]);
  const [usuarios, setUsuarios] = useState([]);
  const [loading, setLoading] = useState(true);
  
  const [showPeriodoForm, setShowPeriodoForm] = useState(false);
  const [showGrupoForm, setShowGrupoForm] = useState(false);
  
  const [periodoForm, setPeriodoForm] = useState({
    nombre: '',
    fecha_inicio: '',
    fecha_fin: ''
  });
  
  const [grupoForm, setGrupoForm] = useState({
    nombre: '',
    descripcion: '',
    nivel_id: '',
    especialidad_id: '',
    periodo_lectivo_id: '',
    docente_ids: [],
    estudiante_ids: []
  });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [periodosRes, gruposRes, nivelesRes, especialidadesRes, usuariosRes] = await Promise.all([
        axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/periodos`, { withCredentials: true }),
        axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/grupos`, { withCredentials: true }),
        axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/niveles`, { withCredentials: true }),
        axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/especialidades`, { withCredentials: true }),
        axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/users`, { withCredentials: true })
      ]);
      
      setPeriodos(periodosRes.data);
      setGrupos(gruposRes.data);
      setNiveles(nivelesRes.data);
      setEspecialidades(especialidadesRes.data);
      setUsuarios(usuariosRes.data);
    } catch (error) {
      console.error('Error fetching data:', error);
      toast.error('Error al cargar datos');
    } finally {
      setLoading(false);
    }
  };

  const handleCreatePeriodo = async (e) => {
    e.preventDefault();
    try {
      await axios.post(
        `${process.env.REACT_APP_BACKEND_URL}/api/periodos`,
        periodoForm,
        { withCredentials: true }
      );
      toast.success('Periodo lectivo creado exitosamente');
      setPeriodoForm({ nombre: '', fecha_inicio: '', fecha_fin: '' });
      setShowPeriodoForm(false);
      fetchData();
    } catch (error) {
      toast.error('Error al crear periodo');
    }
  };

  const handleCreateGrupo = async (e) => {
    e.preventDefault();
    try {
      await axios.post(
        `${process.env.REACT_APP_BACKEND_URL}/api/grupos`,
        grupoForm,
        { withCredentials: true }
      );
      toast.success('Grupo creado exitosamente');
      setGrupoForm({
        nombre: '',
        descripcion: '',
        nivel_id: '',
        especialidad_id: '',
        periodo_lectivo_id: '',
        docente_ids: [],
        estudiante_ids: []
      });
      setShowGrupoForm(false);
      fetchData();
    } catch (error) {
      toast.error('Error al crear grupo');
    }
  };

  return (
    <div className="min-h-screen bg-background">
      <header className="sticky top-0 z-50 backdrop-blur-xl bg-background/80 border-b border-border">
        <div className="container mx-auto px-4 py-4">
          <div className="flex items-center gap-4">
            <Button
              variant="ghost"
              size="icon"
              onClick={() => navigate('/admin')}
              data-testid="back-button"
            >
              <ArrowLeft className="h-5 w-5" />
            </Button>
            <div>
              <h1 className="text-2xl sm:text-3xl font-bold tracking-tight">Gestión de Grupos</h1>
              <p className="text-sm text-muted-foreground">Administrar periodos lectivos y grupos</p>
            </div>
          </div>
        </div>
      </header>

      <main className="container mx-auto px-4 py-8">
        <Tabs defaultValue="periodos" className="space-y-6">
          <TabsList className="grid w-full grid-cols-2">
            <TabsTrigger value="periodos" data-testid="tab-periodos">
              <Calendar className="h-4 w-4 mr-2" />
              Periodos Lectivos
            </TabsTrigger>
            <TabsTrigger value="grupos" data-testid="tab-grupos">
              <Users className="h-4 w-4 mr-2" />
              Grupos
            </TabsTrigger>
          </TabsList>

          {/* PERIODOS LECTIVOS */}
          <TabsContent value="periodos">
            <Card>
              <CardHeader>
                <div className="flex items-center justify-between">
                  <div>
                    <CardTitle>Periodos Lectivos</CardTitle>
                    <CardDescription>Definir ciclos escolares y años lectivos</CardDescription>
                  </div>
                  <Button onClick={() => setShowPeriodoForm(!showPeriodoForm)} data-testid="btn-agregar-periodo">
                    <Plus className="h-4 w-4 mr-2" />
                    Agregar Periodo
                  </Button>
                </div>
              </CardHeader>
              <CardContent>
                {showPeriodoForm && (
                  <form onSubmit={handleCreatePeriodo} className="mb-6 p-4 border border-border rounded-lg space-y-4">
                    <div>
                      <Label htmlFor="periodo-nombre">Nombre del Periodo</Label>
                      <Input
                        id="periodo-nombre"
                        value={periodoForm.nombre}
                        onChange={(e) => setPeriodoForm({ ...periodoForm, nombre: e.target.value })}
                        placeholder="Ej: Primer Semestre 2024"
                        required
                        data-testid="input-periodo-nombre"
                      />
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <Label htmlFor="periodo-inicio">Fecha de Inicio</Label>
                        <Input
                          id="periodo-inicio"
                          type="date"
                          value={periodoForm.fecha_inicio}
                          onChange={(e) => setPeriodoForm({ ...periodoForm, fecha_inicio: e.target.value })}
                          required
                          data-testid="input-periodo-inicio"
                        />
                      </div>
                      <div>
                        <Label htmlFor="periodo-fin">Fecha de Fin</Label>
                        <Input
                          id="periodo-fin"
                          type="date"
                          value={periodoForm.fecha_fin}
                          onChange={(e) => setPeriodoForm({ ...periodoForm, fecha_fin: e.target.value })}
                          required
                          data-testid="input-periodo-fin"
                        />
                      </div>
                    </div>
                    <div className="flex gap-2">
                      <Button type="submit" data-testid="btn-guardar-periodo">Guardar</Button>
                      <Button type="button" variant="outline" onClick={() => setShowPeriodoForm(false)}>Cancelar</Button>
                    </div>
                  </form>
                )}

                {loading ? (
                  <div className="text-center py-8">
                    <div className="animate-spin rounded-full h-12 w-12 border-b-4 border-primary mx-auto"></div>
                  </div>
                ) : periodos.length === 0 ? (
                  <p className="text-center text-muted-foreground py-8">No hay periodos lectivos registrados</p>
                ) : (
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Nombre</TableHead>
                        <TableHead>Fecha Inicio</TableHead>
                        <TableHead>Fecha Fin</TableHead>
                        <TableHead>Estado</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {periodos.map((periodo) => (
                        <TableRow key={periodo.periodo_id}>
                          <TableCell className="font-medium">{periodo.nombre}</TableCell>
                          <TableCell>{new Date(periodo.fecha_inicio).toLocaleDateString()}</TableCell>
                          <TableCell>{new Date(periodo.fecha_fin).toLocaleDateString()}</TableCell>
                          <TableCell>
                            <Badge variant={periodo.activo ? "default" : "secondary"}>
                              {periodo.activo ? 'Activo' : 'Inactivo'}
                            </Badge>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* GRUPOS */}
          <TabsContent value="grupos">
            <Card>
              <CardHeader>
                <div className="flex items-center justify-between">
                  <div>
                    <CardTitle>Grupos</CardTitle>
                    <CardDescription>Organizar estudiantes y docentes en grupos</CardDescription>
                  </div>
                  <Button 
                    onClick={() => setShowGrupoForm(!showGrupoForm)}
                    disabled={periodos.length === 0 || niveles.length === 0 || especialidades.length === 0}
                    data-testid="btn-agregar-grupo"
                  >
                    <Plus className="h-4 w-4 mr-2" />
                    Agregar Grupo
                  </Button>
                </div>
              </CardHeader>
              <CardContent>
                {showGrupoForm && (
                  <form onSubmit={handleCreateGrupo} className="mb-6 p-4 border border-border rounded-lg space-y-4">
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <Label htmlFor="grupo-nombre">Nombre del Grupo</Label>
                        <Input
                          id="grupo-nombre"
                          value={grupoForm.nombre}
                          onChange={(e) => setGrupoForm({ ...grupoForm, nombre: e.target.value })}
                          placeholder="Ej: 10-1"
                          required
                          data-testid="input-grupo-nombre"
                        />
                      </div>
                      <div>
                        <Label htmlFor="grupo-periodo">Periodo Lectivo</Label>
                        <select
                          id="grupo-periodo"
                          className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                          value={grupoForm.periodo_lectivo_id}
                          onChange={(e) => setGrupoForm({ ...grupoForm, periodo_lectivo_id: e.target.value })}
                          required
                          data-testid="select-grupo-periodo"
                        >
                          <option value="">Seleccionar periodo</option>
                          {periodos.map(p => (
                            <option key={p.periodo_id} value={p.periodo_id}>{p.nombre}</option>
                          ))}
                        </select>
                      </div>
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <Label htmlFor="grupo-nivel">Nivel</Label>
                        <select
                          id="grupo-nivel"
                          className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                          value={grupoForm.nivel_id}
                          onChange={(e) => setGrupoForm({ ...grupoForm, nivel_id: e.target.value })}
                          required
                          data-testid="select-grupo-nivel"
                        >
                          <option value="">Seleccionar nivel</option>
                          {niveles.map(n => (
                            <option key={n.nivel_id} value={n.nivel_id}>{n.nombre}</option>
                          ))}
                        </select>
                      </div>
                      <div>
                        <Label htmlFor="grupo-especialidad">Especialidad</Label>
                        <select
                          id="grupo-especialidad"
                          className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                          value={grupoForm.especialidad_id}
                          onChange={(e) => setGrupoForm({ ...grupoForm, especialidad_id: e.target.value })}
                          required
                          data-testid="select-grupo-especialidad"
                        >
                          <option value="">Seleccionar especialidad</option>
                          {especialidades.map(e => (
                            <option key={e.especialidad_id} value={e.especialidad_id}>{e.nombre}</option>
                          ))}
                        </select>
                      </div>
                    </div>
                    <div>
                      <Label htmlFor="grupo-descripcion">Descripción</Label>
                      <Input
                        id="grupo-descripcion"
                        value={grupoForm.descripcion}
                        onChange={(e) => setGrupoForm({ ...grupoForm, descripcion: e.target.value })}
                        placeholder="Descripción opcional"
                        data-testid="input-grupo-descripcion"
                      />
                    </div>
                    <div className="flex gap-2">
                      <Button type="submit" data-testid="btn-guardar-grupo">Guardar</Button>
                      <Button type="button" variant="outline" onClick={() => setShowGrupoForm(false)}>Cancelar</Button>
                    </div>
                  </form>
                )}

                {loading ? (
                  <div className="text-center py-8">
                    <div className="animate-spin rounded-full h-12 w-12 border-b-4 border-primary mx-auto"></div>
                  </div>
                ) : grupos.length === 0 ? (
                  <p className="text-center text-muted-foreground py-8">No hay grupos registrados</p>
                ) : (
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Nombre</TableHead>
                        <TableHead>Nivel</TableHead>
                        <TableHead>Especialidad</TableHead>
                        <TableHead>Periodo</TableHead>
                        <TableHead>Docentes</TableHead>
                        <TableHead>Estudiantes</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {grupos.map((grupo) => {
                        const nivel = niveles.find(n => n.nivel_id === grupo.nivel_id);
                        const especialidad = especialidades.find(e => e.especialidad_id === grupo.especialidad_id);
                        const periodo = periodos.find(p => p.periodo_id === grupo.periodo_lectivo_id);
                        return (
                          <TableRow key={grupo.grupo_id}>
                            <TableCell className="font-medium">{grupo.nombre}</TableCell>
                            <TableCell>{nivel?.nombre || '-'}</TableCell>
                            <TableCell>{especialidad?.nombre || '-'}</TableCell>
                            <TableCell>{periodo?.nombre || '-'}</TableCell>
                            <TableCell>{grupo.docente_ids?.length || 0}</TableCell>
                            <TableCell>{grupo.estudiante_ids?.length || 0}</TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                )}
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </main>
    </div>
  );
};

export default GestionGrupos;
